import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Underlined tabs (Radix) — Field Notes chrome (DESIGN.md §6: active
 * underline is forest, inactive labels sit in `ink-low`). Depth comes
 * from a hairline rule, not a filled segmented track: paper surfaces
 * separate by warmth and lines only (§4).
 *
 * Deep-linkable page-level tabs do not have to be routes: `/organization`
 * drives this component from a `?tab=` search param, which keeps one
 * route while staying linkable and back-button correct. Routes remain
 * right when the sections are separate pages; a search param is right
 * when they are views of one page; local state is right when the
 * selection is not worth a URL (time ranges, sub-panels).
 */
interface TabsProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly children: ReactNode;
  readonly className?: string;
}

export function Tabs({ value, onValueChange, children, className }: TabsProps): JSX.Element {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange} className={className}>
      {children}
    </TabsPrimitive.Root>
  );
}

interface TabsListProps {
  readonly children: ReactNode;
  readonly ariaLabel?: string;
  readonly className?: string;
}

export function TabsList({ children, ariaLabel, className }: TabsListProps): JSX.Element {
  return (
    <TabsPrimitive.List
      aria-label={ariaLabel}
      className={cx('inline-flex items-end gap-5 border-b border-line', className)}
    >
      {children}
    </TabsPrimitive.List>
  );
}

interface TabsTriggerProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly testId?: string;
}

export function TabsTrigger({ value, children, testId }: TabsTriggerProps): JSX.Element {
  return (
    <TabsPrimitive.Trigger
      value={value}
      data-testid={testId}
      className="-mb-px border-b-2 border-transparent px-0.5 pb-2 text-label text-ink-low transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright focus-visible:ring-offset-2 focus-visible:ring-offset-canvas data-[state=active]:border-forest data-[state=active]:text-forest"
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}

interface TabsContentProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps): JSX.Element {
  return (
    <TabsPrimitive.Content value={value} className={cx('focus-visible:outline-none', className)}>
      {children}
    </TabsPrimitive.Content>
  );
}
